import { useState, useMemo } from 'react';
import { useMemoizedFn } from 'ahooks';
import { isArray } from 'lodash-es';
import useIM from 'hooks/business/im/useIM';
import { useAutoTranslateConfig } from 'hooks/business/im/chatPreference';
import { useCurrentConversationLocalVideo } from 'hooks/business/sendMessage';
import { useMsgAttachmentList } from '../Toolbar/AttachmentUpload/hooks/useUploadAttachment';
import { useTextURLExtract } from 'hooks/useTextURLExtract';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import type { LinkQRCodeRef } from '../LinkQRCode';

export interface UseMessageEditorSendParams {
  conversation: Conversation.Conversation;
  conversationId: string;
  input: string;
  trimInput: string;
  targetLang: General.Language;
  onSend?: () => void;
  setInput: (conversationId: string, value: string) => void;
  isInputOverflow: boolean;
  qrcodeRef: React.RefObject<LinkQRCodeRef | null>;
  callTodoCallback: () => void;
  onMercadoChatView?: boolean;
}

/**
 * 消息编辑器发送
 * 单一职责：发送按钮禁用态、执行发送（文本/美客多附件/TikTok 链接二维码）、sending 状态
 */
export function useMessageEditorSend(params: UseMessageEditorSendParams) {
  const {
    conversation: currentConversation,
    conversationId,
    input,
    trimInput,
    targetLang,
    onSend,
    setInput,
    isInputOverflow,
    qrcodeRef,
    callTodoCallback,
    onMercadoChatView = false,
  } = params;

  const { channel, region, controlStatus } = currentConversation;
  const [sending, setSending] = useState(false);
  const { sendText, sendAttachment } = useIM();
  const autoTranslate = useAutoTranslateConfig();
  const localVideoList = useCurrentConversationLocalVideo();
  const msgAttachmentList = useMsgAttachmentList(conversationId);
  const { extract, isLink } = useTextURLExtract();
  const { whiteListConfig: { TTS_NO_QRCODE = false } = {} } = useOrgConfigStateValue();

  const sendableVideoList = useMemo(
    () => localVideoList.filter((x) => x.status === 4),
    [localVideoList],
  );

  const sendBtnDisable = useMemo(() => {
    if (channel === 'MERCADO' && msgAttachmentList.length > 0) {
      const isUploading = msgAttachmentList.some((item) => item.status === 'uploading');
      if (isUploading) return true;
      const isAllSuccess = msgAttachmentList.every((item) => item.status === 'success');
      if (isAllSuccess) return false;
    }
    return (
      sending || !controlStatus || (!trimInput && !sendableVideoList.length) || isInputOverflow
    );
  }, [
    sending,
    channel,
    controlStatus,
    isInputOverflow,
    sendableVideoList.length,
    trimInput,
    msgAttachmentList,
  ]);

  const sendMsgOnMercado = useMemoizedFn((rawText: string, extra: any) => {
    sendAttachment(
      currentConversation,
      {
        text: rawText,
        subject: '',
        originalText: rawText,
        attachments: msgAttachmentList.map(({ data }) => data),
      },
      extra?.autoTranslation,
      onMercadoChatView,
    ).finally(() => {
      setInput(conversationId, '');
      onSend?.();
      callTodoCallback();
    });
  });

  const sendLinkQRCode = useMemoizedFn((rawText: string) => {
    if (!qrcodeRef.current) return;
    const extractText = extract(rawText);
    if (isArray(extractText)) {
      const links = extractText.filter((item) => isLink(item));
      qrcodeRef.current?.sendQRCode(links.map((href) => ({ href, region })));
    } else if (extractText && isLink(extractText)) {
      qrcodeRef.current?.sendQRCode([{ href: extractText, region }]);
    }
  });

  const doSend = useMemoizedFn(async () => {
    setSending(true);
    if (!sendBtnDisable) {
      const rawText = input;
      const extra = autoTranslate
        ? {
            autoTranslation: {
              targetLanguage: targetLang,
              status: 'pending',
              source: 'sender',
            } as Conversation.MsgText['autoTranslation'],
          }
        : undefined;

      if (channel === 'MERCADO') {
        sendMsgOnMercado(rawText, extra);
      } else {
        sendText(conversationId, rawText, extra)
          .then((result) => {
            if (!TTS_NO_QRCODE && result && isTiKTokUpperCase(channel)) {
              sendLinkQRCode(rawText);
            }
          })
          .finally(() => {
            onSend?.();
            callTodoCallback();
          });
      }
    }
    setSending(false);
  });

  return {
    doSend,
    sendBtnDisable,
    sending,
  };
}
